import Controller, { inject as controller } from '@ember/controller';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { v1 } from 'ember-uuid';
import { EMBER_ROUTE_NAMES } from 'sh-www/constants/routes';
import {
  task,
  dropTaskGroup,
  restartableTask,
  timeout,
} from 'ember-concurrency';
import {
  NETWORK_PARAMS_NETWORK_TYPES,
  COMPANY_NETWORK_PARAMS_FILTERS,
  COMPANY_NETWORK_PARAMS_SORT_TYPES,
} from 'sh-www/constants/api/people';
import { USER } from 'sh-www/models/user';

const DEBOUNCE_MS = 300;

/**
 * Defines the cache used in the find freelancer search utility.
 * Cache is used for paging.
 *
 * @returns {Object}
 */
export const findFreelancerCache = () => {
  const cache = new Map();
  const cacheKey = v1();

  return Object.freeze({
    get cacheKey() {
      return cacheKey;
    },

    getPage(page) {
      return cache.get(page);
    },

    setPage(page, value) {
      cache.set(page, value);
      return value;
    },
  });
};

class ProjectCreateFindFreelancerController extends Controller {
  @controller application;

  cache = null;
  meta = null;

  searchResults = [];
  searchText = '';

  /** @type {User|null} */
  profileOverlayUser = null;

  @service session;
  @service shData;
  @service('error-tracker') errorTracker;
  @service('commands/user-commands') userCommandService;

  @dropTaskGroup
  searchResultsUpdates;

  @computed('meta.pagination.total_pages')
  get totalPages() {
    return this.meta?.pagination.total_pages ?? 0;
  }

  @computed('meta.pagination.current_page')
  get currentPage() {
    return this.meta?.pagination.current_page ?? 0;
  }

  @computed('currentPage', 'totalPages')
  get hasNext() {
    const { currentPage, totalPages } = this;

    if (currentPage < totalPages) return true;

    return false;
  }

  @computed('handleNewSearch.isRunning', 'searchResultsUpdates.isRunning')
  get isLoadingPage() {
    return this.handleNewSearch.isRunning || this.searchResultsUpdates.isRunning;
  }

  @computed('searchResults.[]')
  get searchResultsUsers() {
    return this.searchResults.map(({users}) => users).flat();
  }

  @computed('searchResultsUsers.[]', 'searchResultsUpdates.isRunning', 'handleNewSearch.isRunning', 'showCompanyHiredFreelancerSearchPage.lastPerformed')
  get noSearchResultsFound() {
    return !this.searchResultsUsers.length
      && !this.searchResultsUpdates.isRunning
      && !this.handleNewSearch.isRunning
      && this.showCompanyHiredFreelancerSearchPage.lastPerformed
  }

  get fullFreelancerSearchRoute() {
    return EMBER_ROUTE_NAMES.PEOPLE_CENTER__SEARCH;
  }

  canAdvancePage(newPage) {
    return (newPage > 0) && (newPage <= this.totalPages);
  }

  @action
  handleProfileLinkClick(profileOverlay, user) {
    this.set('profileOverlayUser', user);
    this.set('application.profileOverlay', profileOverlay);
  }

  @action
  handleProfileOverlayClose() {
    this.set('application.profileOverlay', null);
  }

  @restartableTask
  *handleNewSearch(inputValue) {
    this.set('searchText', inputValue);

    yield this.searchResultsUpdates.cancelAll();

    // Pause here for DEBOUNCE_MS milliseconds. Because this
    // task is `restartable`, if the user starts typing again,
    // the current search will be canceled at this point and
    // start over from the beginning
    yield timeout(DEBOUNCE_MS);

    this.initializeSearch.perform();
  }

  @task({ group: 'searchResultsUpdates' })
  *initializeSearch() {
    // reset previous search and create cache for new search
    this.setProperties({
      cache: findFreelancerCache(),
      searchResults: [],
      meta: null,
    });

    yield this.showCompanyHiredFreelancerSearchPage.perform(0);
  }

  @task({ group: 'searchResultsUpdates' })
  *updatePage(increment) {
    const newPage = this.currentPage + increment;
    if (this.canAdvancePage(newPage)) {
      yield this.showCompanyHiredFreelancerSearchPage.perform(newPage);
    }
  }

  @restartableTask
  *showCompanyHiredFreelancerSearchPage(page) {
    const cache = this.cache;
    const text = this.searchText;
    const { currentUser } = this.session;

    try {
      const response = yield this.userCommandService
        .searchFreelancerUsers({
          perspectiveUserId: currentUser.id,
          perspectiveCompanyId: currentUser.company.id,
          searchName: text,
          network: NETWORK_PARAMS_NETWORK_TYPES.COMPANY,
          sort: text ? COMPANY_NETWORK_PARAMS_SORT_TYPES.NAME : COMPANY_NETWORK_PARAMS_SORT_TYPES.RECENTLY_HIRED,
          page,
          includes: ['location', `company-seller-connections:in_network_or_extended(${currentUser.company.id}).company`],
          filter: {
            [COMPANY_NETWORK_PARAMS_FILTERS.USER_TYPE]: USER.TYPES.SELLER,
          },
        });

      this.shData.pushResponseToStore(response);
      const freelancerResults = this.shData.getModelArrayFromResponse(response, 'user');

      const searchResults = {
        users: freelancerResults,
      }

      const meta = response.meta;
      cache.setPage(page, { searchResults, meta });

      // update search results in ui and set meta for paging
      this.setProperties({
        searchResults: this.searchResults.concat(searchResults),
        meta,
      });
    } catch(e) {
      this.errorTracker.error(e, this, `error searching for freelancer "${text}" on page ${page}`);
      this.setProperties({
        cache: null,
        searchResults: [],
        meta: null,
      });
    }
  }
}

export default ProjectCreateFindFreelancerController;
